import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Gamepad2, Book, Palette, Compass, ChevronRight, Award, X } from "lucide-react";
import { useApp, API } from "../context";
import { IsaiahMascotCore } from "./isaiah";

/**
 * KidOnboarding, a friendly 3-step walkthrough for first-time kid logins.
 *
 *   Step 1, "Hi, I'm Isaiah!" intro with mascot + name greeting
 *   Step 2, Starter stars reward (+5 ⭐) to hook them
 *   Step 3, Pick your first adventure (Learn / Arcade / Create / Adventures)
 *
 * Coordinates with the rest of the onboarding ecosystem to avoid bombarding
 * the user with overlapping modals:
 *
 *   - Sets `sessionStorage['isaiah_kid_onboarding_active']` while active.
 *     Other flows (WelcomeNewUserModal, FirstMissionPrompt, GettingStartedChecklist)
 *     check this flag and wait.
 *   - Persists completion per-user in `localStorage['isaiah_kid_onboarded_<user_id>']`
 *     AND on `explorer_profiles.kid_onboarded` (server-side) so it survives
 *     device switching.
 *   - Only shows for role=child_explorer on /my-world path.
 *   - The homepage PlatformTour is completely separate (homepage-only).
 */
const SESSION_FLAG = "isaiah_kid_onboarding_active";

const KidOnboarding = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "child_explorer") return;
    // Respect server-side flag first (survives device switching)
    if (user.kid_onboarded) return;
    const localKey = `isaiah_kid_onboarded_${user.id}`;
    if (localStorage.getItem(localKey)) return;
    const t = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem(SESSION_FLAG, "1");
    }, 700);
    return () => clearTimeout(t);
  }, [user]);

  const finish = async (navTo) => {
    if (!user || saving) return;
    setSaving(true);
    const localKey = `isaiah_kid_onboarded_${user.id}`;
    localStorage.setItem(localKey, "1");
    sessionStorage.removeItem(SESSION_FLAG);
    try {
      await axios.post(`${API}/portal/child/${user.id}/onboarded`, {});
    } catch (_) {}
    setVisible(false);
    if (navTo) navigate(navTo);
  };

  const skip = () => finish(null);

  if (!visible || !user) return null;

  const name = user.first_name || user.explorer_name || user.display_name || "friend";

  const worlds = [
    { path: "/learn", label: "Learning Forest", desc: "AI tutor + lessons", icon: Book, color: "emerald", testid: "kid-onb-pick-learn" },
    { path: "/arcade", label: "Arcade City", desc: "25+ fun games", icon: Gamepad2, color: "purple", testid: "kid-onb-pick-arcade" },
    { path: "/adventures", label: "Adventure Island", desc: "AI stories just for you", icon: Compass, color: "amber", testid: "kid-onb-pick-adventures" },
    { path: "/create", label: "Art Studio", desc: "Draw, build, create", icon: Palette, color: "pink", testid: "kid-onb-pick-create" },
  ];

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
      data-testid="kid-onboarding"
      style={{ background: "rgba(0,0,0,0.78)" }}
    >
      <div className="relative max-w-md w-full rounded-3xl border-2 border-purple-400/30 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(30,20,60,0.97), rgba(20,15,50,0.97))",
          boxShadow: "0 20px 60px rgba(139,92,246,0.35)",
        }}>
        {/* Persistent close, kids can always X out and go explore on their own.
            Dismissing marks onboarding complete so it won't show again. */}
        <button
          onClick={skip}
          disabled={saving}
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors disabled:opacity-40"
          aria-label="Close"
          data-testid="kid-onb-close"
        >
          <X size={16} />
        </button>
        <div className="p-6 sm:p-8">

          {/* Step 1, Greeting */}
          {step === 0 && (
            <div className="text-center">
              <div className="w-24 h-28 mx-auto mb-3 relative">
                <IsaiahMascotCore pose="waving" size="icon" style={{ width: 96, height: 110 }} testId="kid-onb-mascot" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-fredoka font-bold text-white mb-2" data-testid="kid-onb-greeting">
                Hi, {name}! 👋
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-sm mx-auto">
                I'm <strong className="text-amber-300">Isaiah</strong>. This is my universe and I'm so excited you're here!
                Let's find something fun to do, ready?
              </p>
              <button
                onClick={() => setStep(1)}
                className="w-full py-3.5 rounded-2xl font-fredoka font-bold text-base text-white transition-transform hover:scale-[1.02]"
                style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899)", boxShadow: "0 4px 20px rgba(139,92,246,0.25)" }}
                data-testid="kid-onb-next-1">
                Yes, let's go! <ChevronRight size={16} className="inline -mt-0.5" />
              </button>
              <button onClick={skip} className="mt-3 text-white/35 hover:text-white/60 text-xs" data-testid="kid-onb-skip">
                Skip for now
              </button>
            </div>
          )}

          {/* Step 2, Starter stars reward */}
          {step === 1 && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-3 rounded-full flex items-center justify-center"
                style={{ background: "radial-gradient(circle, rgba(251,191,36,0.35), rgba(251,191,36,0.05))", animation: "section-rise 0.6s ease-out" }}>
                <Award size={40} className="text-amber-300" />
              </div>
              <h2 className="text-xl sm:text-2xl font-fredoka font-bold text-white mb-2">
                Here are your 5 starter Stars! ⭐
              </h2>
              <p className="text-white/65 text-sm leading-relaxed mb-5 max-w-sm mx-auto">
                Every lesson, game, and adventure earns more Stars. Collect them to level up and unlock cool stuff in
                the <strong className="text-white">Prize Vault</strong>.
              </p>
              <button
                onClick={() => setStep(2)}
                className="w-full py-3.5 rounded-2xl font-fredoka font-bold text-base text-white transition-transform hover:scale-[1.02]"
                style={{ background: "linear-gradient(135deg, #F59E0B, #EF4444, #EC4899)", boxShadow: "0 4px 20px rgba(245,158,11,0.25)" }}
                data-testid="kid-onb-next-2">
                Cool! What's next? <ChevronRight size={16} className="inline -mt-0.5" />
              </button>
            </div>
          )}

          {/* Step 3, Pick world */}
          {step === 2 && (
            <div>
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/15 border border-purple-400/25 mb-2">
                  <Sparkles size={12} className="text-purple-300" />
                  <span className="text-purple-200 text-[10px] font-bold uppercase tracking-widest">Last step</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-fredoka font-bold text-white mb-1">
                  Where do you want to start?
                </h2>
                <p className="text-white/55 text-xs">Pick any one, you can try the others after.</p>
              </div>
              <div className="grid grid-cols-2 gap-2.5" data-testid="kid-onb-worlds">
                {worlds.map((w) => (
                  <button
                    key={w.path}
                    onClick={() => finish(w.path)}
                    disabled={saving}
                    className={`rounded-2xl p-3 border-2 border-${w.color}-400/25 bg-${w.color}-500/[0.09] hover:bg-${w.color}-500/20 hover:border-${w.color}-400/50 transition-all text-left disabled:opacity-60 group`}
                    data-testid={w.testid}>
                    <div className={`w-8 h-8 rounded-lg bg-${w.color}-500/25 flex items-center justify-center mb-2`}>
                      <w.icon size={16} className={`text-${w.color}-300`} />
                    </div>
                    <p className="text-white font-fredoka font-bold text-sm">{w.label}</p>
                    <p className="text-white/45 text-[10px]">{w.desc}</p>
                  </button>
                ))}
              </div>
              <button onClick={skip} className="w-full mt-4 py-2 text-white/35 hover:text-white/60 text-xs font-bold"
                data-testid="kid-onb-explore-later">
                I'll explore on my own
              </button>
            </div>
          )}

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-5" data-testid="kid-onb-progress">
            {[0, 1, 2].map((i) => (
              <div key={i}
                className={`rounded-full transition-all ${i === step ? "w-5 h-2 bg-purple-400" : i < step ? "w-2 h-2 bg-purple-400/40" : "w-2 h-2 bg-white/15"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KidOnboarding;
